import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { PlainDate } from 'temporal-luxon';
import { DatePicker, Input, Select, Spinner, useLocalizeMessage } from 'libs.nucleus';
import { NumericFilterControl } from '../../../dashboards/components/numeric_filter_control';
import { getOperatorLabel, isFreeTextOperator, isNumericFieldType, isRangeOperator } from '../../../dashboards/utils';
import { ReportAnalyticsEvents } from '../../constants/report_analytics_events.constants';
import { useServerSideCubeQuery } from '../../hooks/use_server_side_cube_query';
const PAGE_SIZE = 20;
export const ViewFilter = ({ filterData, selectedOptions = [], setSelectedOptions, selectedOperator, onOperatorChange, logReportEvent, }) => {
    const translate = useLocalizeMessage();
    const isTimeField = filterData.fieldType === 'time';
    const isNumericField = isNumericFieldType(filterData.fieldType);
    const effectiveOperator = filterData.defaultOperator || filterData.operator;
    const isFreeText = isFreeTextOperator(effectiveOperator);
    const displayLabel = useMemo(() => {
        if (isNumericField) {
            return filterData.label;
        }
        const effectiveOp = filterData.defaultOperator || filterData.operator;
        const opLabel = getOperatorLabel(filterData.fieldType, effectiveOp);
        return opLabel ? `${filterData.label} (${translate(opLabel)})` : filterData.label;
    }, [
        filterData.label,
        filterData.fieldType,
        filterData.operator,
        filterData.defaultOperator,
        isNumericField,
        translate,
    ]);
    const [paginationState, setPaginationState] = useState({
        pageIndex: 0,
        pageSize: PAGE_SIZE,
    });
    const [accumulatedOptions, setAccumulatedOptions] = useState([]);
    const [exhausted, setExhausted] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const activeSearchRef = useRef('');
    const prevResultRef = useRef(null);
    const waitingForLoadMoreRef = useRef(false);
    const loadMoreResolveRef = useRef(null);
    const query = useMemo(() => {
        if (isTimeField) {
            return filterData.query;
        }
        const baseQuery = { ...filterData.query, renewQuery: true };
        const filters = [...(baseQuery.filters || [])];
        if (searchTerm.trim()) {
            filters.push({
                member: filterData.key,
                operator: 'contains',
                values: [searchTerm.trim()],
            });
        }
        return { ...baseQuery, filters };
    }, [filterData.query, filterData.key, searchTerm, isTimeField]);
    const filterLoadLoggedRef = useRef(false);
    const { resultSet, isLoading, error, total } = useServerSideCubeQuery(query, {
        pagination: paginationState,
        skip: isTimeField || isNumericField || isFreeText,
        onQueryExecuted: () => {
            logReportEvent?.(ReportAnalyticsEvents.QUERY_EXECUTED, {
                query_source: 'filter',
                filter_key: filterData.key,
            });
        },
    });
    const hasMore = !exhausted && total > 0 && accumulatedOptions.length < total;
    // Analytics: filter load lifecycle
    useEffect(() => {
        logReportEvent?.(ReportAnalyticsEvents.FILTER_LOAD_STARTED, {
            filter_key: filterData.key,
            filter_label: filterData.label,
        });
        filterLoadLoggedRef.current = false;
    }, [filterData.key]);
    useEffect(() => {
        if (filterLoadLoggedRef.current || isLoading) {
            return;
        }
        if (error) {
            filterLoadLoggedRef.current = true;
            logReportEvent?.(ReportAnalyticsEvents.FILTER_LOAD_FAILED, {
                filter_key: filterData.key,
                filter_label: filterData.label,
                error_message: error.message,
            });
        }
        else if (resultSet && paginationState.pageIndex === 0) {
            filterLoadLoggedRef.current = true;
            logReportEvent?.(ReportAnalyticsEvents.FILTER_LOAD_COMPLETED, {
                filter_key: filterData.key,
                filter_label: filterData.label,
                options_count: total,
            });
        }
    }, [isLoading, error, resultSet]);
    useEffect(() => {
        if (!isLoading && waitingForLoadMoreRef.current && loadMoreResolveRef.current) {
            waitingForLoadMoreRef.current = false;
            const resolve = loadMoreResolveRef.current;
            loadMoreResolveRef.current = null;
            resolve();
        }
    }, [isLoading]);
    useEffect(() => {
        if (isTimeField || !resultSet || resultSet === prevResultRef.current) {
            return;
        }
        prevResultRef.current = resultSet;
        const rawRows = resultSet.tablePivot();
        const newOptions = rawRows
            .map((row) => {
            const value = row[filterData.key];
            if (value === null || value === undefined) {
                return null;
            }
            return { label: String(value), value: String(value) };
        })
            .filter(Boolean);
        const isLastPage = rawRows.length < PAGE_SIZE;
        if (paginationState.pageIndex === 0) {
            setExhausted(isLastPage);
            if (filterData.defaultValues?.length) {
                const loadedValueSet = new Set(newOptions.map((o) => o.value));
                const missingDefaults = filterData.defaultValues
                    .filter((v) => !loadedValueSet.has(v))
                    .map((v) => ({ label: v, value: v }));
                setAccumulatedOptions([...missingDefaults, ...newOptions]);
            }
            else {
                setAccumulatedOptions(newOptions);
            }
        }
        else {
            if (newOptions.length > 0) {
                setAccumulatedOptions((prev) => [...prev, ...newOptions]);
            }
            if (isLastPage || newOptions.length === 0) {
                setExhausted(true);
            }
        }
    }, [resultSet, filterData.key, paginationState.pageIndex, filterData.defaultValues, isTimeField]);
    const handleLoadMore = useCallback(async () => {
        if (!hasMore || isLoading) {
            return;
        }
        waitingForLoadMoreRef.current = true;
        logReportEvent?.(ReportAnalyticsEvents.FILTER_LOAD_MORE, {
            filter_key: filterData.key,
            filter_label: filterData.label,
            page_index: paginationState.pageIndex + 1,
        });
        return new Promise((resolve) => {
            loadMoreResolveRef.current = resolve;
            setPaginationState((prev) => ({ ...prev, pageIndex: prev.pageIndex + 1 }));
        });
    }, [hasMore, isLoading, paginationState.pageIndex, filterData.key, filterData.label, logReportEvent]);
    const handleLoadMoreRef = useRef(handleLoadMore);
    handleLoadMoreRef.current = handleLoadMore;
    /** Stable identity so Nucleus Select's auto fill-viewport effect is not retriggered every page. */
    const stableLoadMore = useCallback(() => handleLoadMoreRef.current(), []);
    const handleSearchOptions = useCallback((term) => {
        if (term === activeSearchRef.current) {
            return;
        }
        activeSearchRef.current = term;
        logReportEvent?.(ReportAnalyticsEvents.FILTER_SEARCH, {
            filter_key: filterData.key,
            filter_label: filterData.label,
        });
        if (waitingForLoadMoreRef.current && loadMoreResolveRef.current) {
            waitingForLoadMoreRef.current = false;
            const resolve = loadMoreResolveRef.current;
            loadMoreResolveRef.current = null;
            resolve();
        }
        setExhausted(false);
        setPaginationState({ pageIndex: 0, pageSize: PAGE_SIZE });
        setAccumulatedOptions([]);
        setSearchTerm(term);
    }, []);
    const selectionLabel = useMemo(() => {
        const labels = selectedOptions.map(({ label }) => label);
        if (labels.length > 1) {
            return `${labels.slice(0, 1).join(', ')} (+${labels.length - 1} more)`;
        }
        return labels.join(', ');
    }, [selectedOptions]);
    const isDateRange = isTimeField && isRangeOperator(selectedOperator);
    const parseDateValue = (raw) => {
        if (!raw) {
            return undefined;
        }
        try {
            return PlainDate.from(raw.split('T')[0]);
        }
        catch {
            return undefined;
        }
    };
    const singleDateValue = useMemo(() => {
        if (!isTimeField || isDateRange) {
            return undefined;
        }
        return parseDateValue(selectedOptions[0]?.value);
    }, [isTimeField, isDateRange, selectedOptions]);
    const fromDateValue = useMemo(() => (isDateRange ? parseDateValue(selectedOptions[0]?.value) : undefined), [isDateRange, selectedOptions]);
    const toDateValue = useMemo(() => (isDateRange ? parseDateValue(selectedOptions[1]?.value) : undefined), [isDateRange, selectedOptions]);
    const handleDateChange = useCallback((date) => {
        if (date) {
            const isoValue = date.toString();
            setSelectedOptions([{ label: isoValue, value: isoValue }]);
        }
        else {
            setSelectedOptions([]);
        }
    }, [setSelectedOptions]);
    const handleRangeDateChange = useCallback((which, date) => {
        const iso = date?.toString() ?? '';
        const currentFrom = selectedOptions[0]?.value ?? '';
        const currentTo = selectedOptions[1]?.value ?? '';
        const nextFrom = which === 'from' ? iso : currentFrom;
        const nextTo = which === 'to' ? iso : currentTo;
        if (!nextFrom && !nextTo) {
            setSelectedOptions([]);
            return;
        }
        setSelectedOptions([
            { label: nextFrom, value: nextFrom },
            { label: nextTo, value: nextTo },
        ]);
    }, [setSelectedOptions, selectedOptions]);
    const isInitialLoad = isLoading && accumulatedOptions.length === 0;
    if (isTimeField) {
        return (_jsxs("div", { children: [displayLabel && (_jsx("div", { className: 'nucleus-font-medium nucleus-text-sm nucleus-text-text-primary nucleus-flex-none nucleus-order-none nucleus-grow-0 nucleus-block nucleus-mb-2', children: displayLabel })), isDateRange ? (_jsxs("div", { className: 'grid grid-cols-2 gap-2', children: [_jsx(DatePicker, { id: `date-filter-from-${filterData.key}`, value: fromDateValue, onChange: (date) => handleRangeDateChange('from', date), width: 'full' }), _jsx(DatePicker, { id: `date-filter-to-${filterData.key}`, value: toDateValue, onChange: (date) => handleRangeDateChange('to', date), width: 'full' })] })) : (_jsx(DatePicker, { id: `date-filter-${filterData.key}`, value: singleDateValue, onChange: handleDateChange, width: 'sm' }))] }));
    }
    if (isNumericField) {
        const numericValue = selectedOptions[0]?.value ?? '';
        return (_jsx(NumericFilterControl, { filterKey: filterData.key, label: displayLabel, operator: selectedOperator, onOperatorChange: onOperatorChange, value: numericValue, onValueChange: (next) => setSelectedOptions(next ? [{ label: next, value: next }] : []), inputWidth: 'sm' }));
    }
    if (isFreeText) {
        const freeTextValue = selectedOptions[0]?.value ?? '';
        return (_jsxs("div", { children: [displayLabel && (_jsx("div", { className: 'nucleus-font-medium nucleus-text-sm nucleus-text-text-primary nucleus-flex-none nucleus-order-none nucleus-grow-0 nucleus-block nucleus-mb-2', children: displayLabel })), _jsx(Input, { id: `text-filter-${filterData.key}`, value: freeTextValue, onChange: (e) => {
                        const next = e.target.value;
                        setSelectedOptions(next ? [{ label: next, value: next }] : []);
                    }, width: 'sm', placeholder: translate('Enter text to match') })] }));
    }
    return (_jsxs("div", { className: 'relative', children: [isInitialLoad && (_jsx("div", { className: 'absolute z-[5] inset-0 flex items-end justify-center size-full pb-1', children: _jsx(Spinner, {}) })), displayLabel && (_jsx("div", { className: 'nucleus-font-medium nucleus-text-sm nucleus-text-text-primary nucleus-flex-none nucleus-order-none nucleus-grow-0 nucleus-block nucleus-mb-2', children: displayLabel })), !isLoading && error && (_jsxs("div", { className: 'max-w-[250px]', children: [translate('No data'), " -- ", error?.message] })), !error && (_jsx(Select, { disabled: isInitialLoad, options: accumulatedOptions, value: selectedOptions, onChange: setSelectedOptions, selectionLabel: selectionLabel, width: 'sm', multiple: true, searchableOptions: true, searchPlaceholder: translate('Search and press Enter...'), onSearchOptions: handleSearchOptions, onLoadMore: hasMore ? stableLoadMore : undefined }, filterData.key))] }));
};
